import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, UserCheck, Package, Pencil, Trash2, RefreshCw, Users, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [activeRiderTab, setActiveRiderTab] = useState("all");
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [formData, setFormData] = useState({
    customer_name: "",
    customer_phone: "",
    order_details: "",
    delivery_charge: "",
    is_cod: false,
    cod_amount: "",
  });

  const [selectedRiderId, setSelectedRiderId] = useState("");
  const [createRiderId, setCreateRiderId] = useState("none");
  const activeTabRider = riders.find((r) => r.id === activeRiderTab);
  const createRider = riders.find((r) => r.id === createRiderId);

  const openCreateDialog = (open) => {
    if (open) setCreateRiderId(activeTabRider ? activeTabRider.id : "none");
    setCreateDialogOpen(open);
  };

  // Get current day name in IST
  const getTodayIST = () => {
    const now = new Date();
    return now.toLocaleDateString("en-US", { weekday: "long", timeZone: "Asia/Kolkata" });
  };

  const isRiderOffDay = (rider) => {
    if (!rider?.no_service_day) return false;
    return rider.no_service_day === getTodayIST();
  };

  useEffect(() => {
    fetchOrders();
    fetchRiders();
    fetchCustomerSuggestions();

    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchOrders();
      }, 5000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchRiders = async () => {
    try {
      const response = await axios.get(`${API}/riders`);
      setRiders(response.data.filter((r) => r.active));
    } catch (error) {
      console.error("Error fetching riders:", error);
    }
  };

  const fetchCustomerSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/customers/suggestions`);
      setCustomerSuggestions(response.data);
    } catch (error) {
      console.error("Error fetching customer suggestions:", error);
    }
  };

  const filteredSuggestions = formData.customer_name.length >= 1
    ? customerSuggestions.filter((c) =>
        c.name.toLowerCase().includes(formData.customer_name.toLowerCase())
      )
    : [];

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    try {
      const { data: created } = await axios.post(`${API}/orders`, {
        ...formData,
        delivery_charge: parseFloat(formData.delivery_charge),
        cod_amount: formData.is_cod ? parseFloat(formData.cod_amount || 0) : 0,
      });
      if (createRider) {
        try {
          await axios.put(`${API}/orders/${created.id}/assign`, { rider_id: createRider.id });
          toast.success(`Order created & assigned to ${createRider.name}`);
        } catch {
          toast.error("Order created, but assigning failed — use Assign on the order");
        }
      } else {
        toast.success("Order created successfully");
      }
      setCreateDialogOpen(false);
      setFormData({
        customer_name: "",
        customer_phone: "",
        order_details: "",
        delivery_charge: "",
        is_cod: false,
        cod_amount: "",
      });
      fetchOrders();
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Failed to create order");
    }
  };

  const handleAssignOrder = async () => {
    if (!selectedRiderId) {
      toast.error("Please select a rider");
      return;
    }

    try {
      await axios.put(`${API}/orders/${selectedOrder.id}/assign`, {
        rider_id: selectedRiderId,
      });
      toast.success("Order assigned successfully");
      setAssignDialogOpen(false);
      setSelectedOrder(null);
      setSelectedRiderId("");
      fetchOrders();
    } catch (error) {
      console.error("Error assigning order:", error);
      toast.error("Failed to assign order");
    }
  };

  const handleEditOrder = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/orders/${selectedOrder.id}`, {
        ...formData,
        delivery_charge: parseFloat(formData.delivery_charge),
        cod_amount: formData.is_cod ? parseFloat(formData.cod_amount || 0) : 0,
      });
      toast.success("Order updated successfully");
      setEditDialogOpen(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error("Failed to update order");
    }
  };

  const handleDeleteOrder = async (orderId, customerName) => {
    if (!window.confirm(`Are you sure you want to delete order for ${customerName}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success("Order deleted successfully");
      fetchOrders();
    } catch (error) {
      console.error("Error deleting order:", error);
      toast.error("Failed to delete order");
    }
  };

  const openEditDialog = (order) => {
    setSelectedOrder(order);
    setFormData({
      customer_name: order.customer_name,
      customer_phone: order.customer_phone,
      order_details: order.order_details || order.delivery_address || "",
      delivery_charge: order.delivery_charge.toString(),
      is_cod: order.is_cod || false,
      cod_amount: order.cod_amount ? order.cod_amount.toString() : "",
    });
    setEditDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-800 border-amber-200",
      assigned: "bg-blue-100 text-blue-800 border-blue-200",
      delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
      cancelled: "bg-red-100 text-red-800 border-red-200",
    };
    return (
      <span
        className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${styles[status] || "bg-slate-100 text-slate-800"}`}
      >
        {status.toUpperCase()}
      </span>
    );
  };

  // Filter orders by active rider tab
  const filteredOrders = activeRiderTab === "all"
    ? orders
    : activeRiderTab === "unassigned"
    ? orders.filter((o) => !o.rider_id)
    : orders.filter((o) => o.rider_id === activeRiderTab);

  // Count orders per rider
  const riderOrderCounts = riders.reduce((acc, r) => {
    acc[r.id] = orders.filter((o) => o.rider_id === r.id).length;
    return acc;
  }, {});
  const unassignedCount = orders.filter((o) => !o.rider_id).length;

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="orders-title">
            Orders
          </h1>
          <p className="text-slate-500 mt-2">Manage delivery orders</p>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-md">
            <RefreshCw size={16} className={autoRefresh ? "text-green-600 animate-spin" : "text-slate-400"} />
            <span className="text-sm text-slate-600">Auto-refresh</span>
            <Switch
              checked={autoRefresh}
              onCheckedChange={setAutoRefresh}
              data-testid="auto-refresh-toggle"
            />
          </div>

          <Dialog open={createDialogOpen} onOpenChange={openCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 text-white hover:bg-orange-600 shadow-sm" data-testid="create-order-btn">
                <Plus size={20} className="mr-2" />
                {activeTabRider ? `Create Order for ${activeTabRider.name}` : "Create Order"}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-order-dialog">
            <DialogHeader>
              <DialogTitle>Create New Order</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateOrder} className="space-y-4">
              <div className="relative">
                <Label htmlFor="customer_name">Customer Name</Label>
                <Input
                  id="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => {
                    setFormData({ ...formData, customer_name: e.target.value });
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  autoComplete="off"
                  required
                  data-testid="input-customer-name"
                />
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-y-auto" data-testid="customer-suggestions">
                    {filteredSuggestions.map((c, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-orange-50 flex items-center justify-between text-sm transition-colors"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setFormData({ ...formData, customer_name: c.name, customer_phone: c.phone });
                          setShowSuggestions(false);
                        }}
                        data-testid={`customer-suggestion-${idx}`}
                      >
                        <span className="font-medium text-slate-900">{c.name}</span>
                        <span className="text-slate-500">{c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="customer_phone">Customer Phone</Label>
                <Input
                  id="customer_phone"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  required
                  data-testid="input-customer-phone"
                />
              </div>
              <div>
                <Label htmlFor="order_details">Order Details</Label>
                <Textarea
                  id="order_details"
                  value={formData.order_details}
                  onChange={(e) => setFormData({ ...formData, order_details: e.target.value })}
                  placeholder="Delivery address, item details, special instructions..."
                  rows={3}
                  required
                  data-testid="input-order-details"
                />
              </div>
              <div>
                <Label htmlFor="delivery_charge">Delivery Charge (₹)</Label>
                <Input
                  id="delivery_charge"
                  type="number"
                  step="0.01"
                  value={formData.delivery_charge}
                  onChange={(e) => setFormData({ ...formData, delivery_charge: e.target.value })}
                  required
                  data-testid="input-delivery-charge"
                />
              </div>
              
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label htmlFor="is_cod" className="text-base">Cash on Delivery (COD)</Label>
                    <p className="text-xs text-slate-500 mt-1">Enable if customer will pay cash upon delivery</p>
                  </div>
                  <Switch
                    id="is_cod"
                    checked={formData.is_cod}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_cod: checked })}
                    data-testid="switch-cod"
                  />
                </div>
                
                {formData.is_cod && (
                  <div>
                    <Label htmlFor="cod_amount">COD Amount (₹)</Label>
                    <Input
                      id="cod_amount"
                      type="number"
                      step="0.01"
                      value={formData.cod_amount}
                      onChange={(e) => setFormData({ ...formData, cod_amount: e.target.value })}
                      required={formData.is_cod}
                      placeholder="Enter COD amount"
                      data-testid="input-cod-amount"
                    />
                    <p className="text-xs text-slate-500 mt-1">Amount rider will collect from customer</p>
                  </div>
                )}
              </div>
              
              <div className="border-t pt-4">
                <Label>Assign to Rider</Label>
                <Select value={createRiderId} onValueChange={setCreateRiderId}>
                  <SelectTrigger data-testid="create-select-rider-trigger">
                    <SelectValue placeholder="Choose a rider" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="create-rider-option-none">Don't assign yet</SelectItem>
                    {riders.map((rider) => (
                      <SelectItem key={rider.id} value={rider.id} data-testid={`create-rider-option-${rider.id}`}>
                        {rider.name} - {rider.vehicle_number}{isRiderOffDay(rider) ? " (Off today)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {createRider && isRiderOffDay(createRider) && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-testid="create-no-service-day-warning">
                    <AlertTriangle size={12} /> Today is {createRider.name}'s off day ({createRider.no_service_day})
                  </p>
                )}
              </div>

              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="submit-order-btn">
                {createRider ? `Create & Assign to ${createRider.name}` : "Create Order"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Rider Tabs */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="rider-tabs">
        <button
          onClick={() => setActiveRiderTab("all")}
          className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
            activeRiderTab === "all"
              ? "bg-slate-900 text-white shadow-sm"
              : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
          }`}
          data-testid="rider-tab-all"
        >
          All Orders ({orders.length})
        </button>
        {unassignedCount > 0 && (
          <button
            onClick={() => setActiveRiderTab("unassigned")}
            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              activeRiderTab === "unassigned"
                ? "bg-amber-500 text-white shadow-sm"
                : "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
            }`}
            data-testid="rider-tab-unassigned"
          >
            Unassigned ({unassignedCount})
          </button>
        )}
        {riders.map((rider) => (
          <button
            key={rider.id}
            onClick={() => setActiveRiderTab(rider.id)}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              activeRiderTab === rider.id
                ? "bg-orange-500 text-white shadow-sm"
                : isRiderOffDay(rider)
                ? "bg-white text-slate-600 border border-red-200 hover:bg-red-50"
                : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
            data-testid={`rider-tab-${rider.id}`}
          >
            {rider.name} ({riderOrderCounts[rider.id] || 0})
            {isRiderOffDay(rider) && (
              <span className={`text-xs font-medium ${activeRiderTab === rider.id ? "text-red-200" : "text-red-500"}`}>
                OFF
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Orders Table */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-md overflow-hidden" data-testid="orders-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs uppercase font-semibold text-slate-500">Customer</th>
                <th className="text-left px-6 py-3 text-xs uppercase font-semibold text-slate-500">Order Details</th>
                <th className="text-left px-6 py-3 text-xs uppercase font-semibold text-slate-500">Rider</th>
                <th className="text-left px-6 py-3 text-xs uppercase font-semibold text-slate-500">Charge</th>
                <th className="text-left px-6 py-3 text-xs uppercase font-semibold text-slate-500">COD</th>
                <th className="text-left px-6 py-3 text-xs uppercase font-semibold text-slate-500">Status</th>
                <th className="text-left px-6 py-3 text-xs uppercase font-semibold text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-slate-500">
                    {activeRiderTab === "all" ? "No orders found" : "No orders for this rider"}
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-slate-50 transition-colors" data-testid="order-row">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{order.customer_name}</p>
                        <p className="text-xs text-slate-500">{order.customer_phone}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-slate-700 line-clamp-2 max-w-xs">
                        {order.order_details || order.delivery_address || "-"}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-900">
                      {order.rider_name || <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                      ₹{order.delivery_charge.toFixed(2)}
                    </td>
                    <td className="px-6 py-4">
                      {order.is_cod ? (
                        <div>
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                            COD
                          </span>
                          <p className="text-xs text-slate-600 mt-1">₹{(order.cod_amount || 0).toFixed(2)}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">{getStatusBadge(order.status)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {order.status === "pending" && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedOrder(order);
                              setAssignDialogOpen(true);
                            }}
                            className="bg-blue-500 text-white hover:bg-blue-600"
                            data-testid="assign-order-btn"
                          >
                            <UserCheck size={16} className="mr-1" />
                            Assign
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditDialog(order)}
                          className="border-slate-300 hover:bg-slate-50"
                          data-testid="edit-order-btn"
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteOrder(order.id, order.customer_name)}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          data-testid="delete-order-btn"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign Order Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent data-testid="assign-order-dialog">
          <DialogHeader>
            <DialogTitle>Assign Order to Rider</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-md">
                <p className="text-sm font-medium text-slate-900">
                  Customer: {selectedOrder.customer_name}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  {selectedOrder.order_details || selectedOrder.delivery_address || ""}
                </p>
              </div>
              <div>
                <Label>Select Rider</Label>
                <Select value={selectedRiderId} onValueChange={setSelectedRiderId}>
                  <SelectTrigger data-testid="select-rider-trigger">
                    <SelectValue placeholder="Choose a rider" />
                  </SelectTrigger>
                  <SelectContent>
                    {riders.map((rider) => (
                      <SelectItem key={rider.id} value={rider.id} data-testid={`rider-option-${rider.id}`}>
                        <span className="flex items-center gap-2">
                          {rider.name} - {rider.vehicle_number}
                          {isRiderOffDay(rider) && (
                            <span className="text-xs text-red-500 font-medium">(Off today)</span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* No Service Day Warning */}
              {selectedRiderId && isRiderOffDay(riders.find((r) => r.id === selectedRiderId)) && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md" data-testid="no-service-day-warning">
                  <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800">
                      Today is {riders.find((r) => r.id === selectedRiderId)?.name}'s off day ({riders.find((r) => r.id === selectedRiderId)?.no_service_day})
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      This rider's vehicle is restricted today. You can still assign if needed.
                    </p>
                  </div>
                </div>
              )}
              <Button
                onClick={handleAssignOrder}
                className={`w-full ${
                  selectedRiderId && isRiderOffDay(riders.find((r) => r.id === selectedRiderId))
                    ? "bg-amber-500 hover:bg-amber-600"
                    : "bg-orange-500 hover:bg-orange-600"
                }`}
                data-testid="confirm-assign-btn"
              >
                {selectedRiderId && isRiderOffDay(riders.find((r) => r.id === selectedRiderId))
                  ? "Assign Anyway"
                  : "Assign Order"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent data-testid="edit-order-dialog">
          <DialogHeader>
            <DialogTitle>Edit Order</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <form onSubmit={handleEditOrder} className="space-y-4">
              <div>
                <Label htmlFor="edit_customer_name">Customer Name</Label>
                <Input
                  id="edit_customer_name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                  data-testid="edit-input-customer-name"
                />
              </div>
              <div>
                <Label htmlFor="edit_customer_phone">Customer Phone</Label>
                <Input
                  id="edit_customer_phone"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  required
                  data-testid="edit-input-customer-phone"
                />
              </div>
              <div>
                <Label htmlFor="edit_order_details">Order Details</Label>
                <Textarea
                  id="edit_order_details"
                  value={formData.order_details}
                  onChange={(e) => setFormData({ ...formData, order_details: e.target.value })}
                  placeholder="Delivery address, item details, special instructions..."
                  rows={3}
                  required
                  data-testid="edit-input-order-details"
                />
              </div>
              <div>
                <Label htmlFor="edit_delivery_charge">Delivery Charge (₹)</Label>
                <Input
                  id="edit_delivery_charge"
                  type="number"
                  step="0.01"
                  value={formData.delivery_charge}
                  onChange={(e) => setFormData({ ...formData, delivery_charge: e.target.value })}
                  required
                  data-testid="edit-input-delivery-charge"
                />
              </div>
              
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label htmlFor="edit_is_cod" className="text-base">Cash on Delivery (COD)</Label>
                    <p className="text-xs text-slate-500 mt-1">Enable if customer will pay cash upon delivery</p>
                  </div>
                  <Switch
                    id="edit_is_cod"
                    checked={formData.is_cod}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_cod: checked })}
                    data-testid="edit-switch-cod"
                  />
                </div>
                
                {formData.is_cod && (
                  <div>
                    <Label htmlFor="edit_cod_amount">COD Amount (₹)</Label>
                    <Input
                      id="edit_cod_amount"
                      type="number"
                      step="0.01"
                      value={formData.cod_amount}
                      onChange={(e) => setFormData({ ...formData, cod_amount: e.target.value })}
                      required={formData.is_cod}
                      placeholder="Enter COD amount"
                      data-testid="edit-input-cod-amount"
                    />
                  </div>
                )}
              </div>
              
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="submit-edit-btn">
                Update Order
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;
